import { getQuoteCheapestPrice, getQuoteConstraints, type Quote } from '../../../modules/quotes'
import { type QuoteBranch } from '../../../modules/quotes/core/models/subModels/quoteBranch.model'
import { type ConstraintsFilter } from './quotesView.types'

/**
 * cheapest price on top (date doesn't matter)
 */
export const byCheapestPrice = (quotes: Quote[]) => {
  const quotesCheapestPrice: Array<[Quote, number]> = quotes.map(quote => ([
    quote,
    getQuoteCheapestPrice(quote)?.price ?? quote.subtotal.price,
  ]))

  quotesCheapestPrice.sort(([a, aCheapest], [b, bCheapest]) => {
    return (
      aCheapest - bCheapest
    )
  })
  return quotesCheapestPrice.map(([quote]) => quote)
}

/**
 * cheapest price on top for the request date
 */
export const byPrice = (quotes: Quote[]) => {
  quotes.sort((a, b) => {
    return (
      a.subtotal.price - b.subtotal.price
    )
  })
}

/**
 * best company rating on top
 * when equal, use the number of rating
 */
export const byRating = (quotes: Quote[]) => {
  const getRating = (companyBranch: QuoteBranch): [number, number] => {
    const rating = companyBranch.ratings?.movingWaldo ?? companyBranch.ratings?.google
    const isMw = !!companyBranch.ratings?.movingWaldo
    const ratio = isMw ? 50 : 1

    return [
      rating?.rating ?? 0,
      (rating?.nbRatings ?? 0) * ratio,
    ]
  }

  const companiesRatings = quotes
    .map(quote => getRating(quote.companyBranch))

  const allRatings = companiesRatings
    .reduce((acc, curr) => [acc[0] + (curr[0] * curr[1]), acc[1] + curr[1]], [0, 0])

  const getMedianRating = () => {
    const sortedRatings = companiesRatings
      .map(([rating]) => rating)
      .sort((a, b) => a - b)
    const length = sortedRatings.length
    const middle = Math.floor(length / 2)
    if (length % 2 === 0) {
      return (sortedRatings[middle - 1] + sortedRatings[middle]) / 2
    }
    return sortedRatings[middle]
  }

  const anchorValue = Math.min(getMedianRating(), 4.5)
  const averageNbOfRatings = allRatings[1] / quotes.length

  /**
   * Bayesian Average
   */
  const getWeightedRating = (companyBranch: QuoteBranch) => {
    const [rating, nbRating] = getRating(companyBranch)
    return (((anchorValue * averageNbOfRatings) + (rating * nbRating)) / (averageNbOfRatings + nbRating))
  }

  quotes.sort((a, b) => {
    const ratingA = getWeightedRating(a.companyBranch)
    const ratingB = getWeightedRating(b.companyBranch)

    if (ratingA > ratingB) {
      return -1
    }
    if (ratingA < ratingB) {
      return 1
    }
    return 0
  })
  return quotes
}

/**
 * less constraints on top
 */
export const byConstraints = (quotes: Quote[], filter?: ConstraintsFilter) => {
  quotes.sort((a, b) => {
    const constraintsA = (filter?.(a) ?? getQuoteConstraints(a))?.length ?? 0
    const constraintsB = (filter?.(b) ?? getQuoteConstraints(b))?.length ?? 0

    return (
      constraintsA - constraintsB
    )
  })
  return quotes
}
